import { ClaimItemCorrectionResponseDTOActionEnum } from '@reposit/api-client';
import { startCase, toLower } from 'lodash';
import numeral from 'numeral';
import React, { useState } from 'react';
import styled from 'styled-components';
import { NumberFormat } from '../../constants/number-formats';
import { PendingInternalUpdate } from '../../redux/claim-item/claim-item.types';
import Button from '../Button';
import IdentifierValue from '../IdentifierValue';
import Modal from '../Modal';
import { Header3, P2, P3 } from '../Typography';

interface ClaimSummaryInfo {
  id: string;
  address: string;
  status: string;
}

interface ClaimItemEditModalProps {
  claimItemId: string;
  currentAmount: number;
  title?: string;
  itemType?: string;
  claimSummary?: ClaimSummaryInfo;
  pendingPreview?: PendingInternalUpdate;
  isPreviewing?: boolean;
  isConfirming?: boolean;
  isEditDisabled?: boolean;
  onReduce: (claimItemId: string, newAmount: number) => void;
  onRemove: (claimItemId: string) => void;
  onConfirm: () => void;
  onGoBack: () => void;
  onDismiss: () => void;
}

// ─── Item summary ─────────────────────────────────────────────────────────────

const ItemSummary = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  padding: 22px 14px 12px;
  position: relative;
`;

const CardTypePill = styled.span`
  background: ${(props) => props.theme.colors.bgHoverAccent};
  border-bottom-right-radius: 4px;
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

const SectionDivider = styled.div<{ deletion?: boolean }>`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  margin-top: 4px;

  &::before,
  &::after {
    background: ${(props) => (props.deletion ? props.theme.colors.negative : '#F5A623')};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionDividerText = styled.span<{ deletion?: boolean }>`
  color: ${(props) => (props.deletion ? props.theme.colors.negative : '#B87A00')};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ItemSummaryTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ItemSummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemSummaryName = styled.span<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
`;

const ClaimSummaryRight = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
  margin-left: 12px;
`;

const StatusChange = styled.span`
  align-items: center;
  color: ${(props) => props.theme.colors.negative};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
`;

const DeactivationWarning = styled.p`
  background: #fff4f6;
  border: 1px solid ${(props) => props.theme.colors.negative};
  border-radius: 8px;
  color: ${(props) => props.theme.colors.negative};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  margin: 4px 0 16px;
  padding: 12px 14px;
`;

const ItemSummaryAmount = styled.span<{ deleted?: boolean }>`
  align-items: center;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
`;

const SummaryOldAmount = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-weight: 400;
  text-decoration: line-through;
`;

const SummaryArrow = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-size: 13px;
  font-weight: 400;
`;

const ItemSummarySubRow = styled.div<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.placeholder};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  margin-top: 4px;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
`;

// ─── Currency input ───────────────────────────────────────────────────────────

const CurrencyInputWrapper = styled.div`
  align-items: stretch;
  border: 1px solid ${(props) => props.theme.colors.inactive};
  border-radius: 4px;
  display: flex;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${(props) => props.theme.colors.primaryBrand};
  }
`;

const CurrencySymbol = styled.span`
  align-items: center;
  background: ${(props) => props.theme.colors.bgAccent};
  border-right: 1px solid ${(props) => props.theme.colors.inactive};
  color: ${(props) => props.theme.colors.body};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 600;
  padding: 0 12px;
`;

const CurrencyInput = styled.input`
  border: none;
  color: ${(props) => props.theme.colors.body};
  flex: 1;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  outline: none;
  padding: 10px 12px;
  width: 100%;

  &::placeholder {
    color: ${(props) => props.theme.colors.placeholder};
  }

  &:disabled {
    background: ${(props) => props.theme.colors.bgAccent};
    color: ${(props) => props.theme.colors.disabled};
  }

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

// ─── Shared layout ────────────────────────────────────────────────────────────

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 20px 0;
`;

const SectionTitle = styled.p`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 4px;
`;

const SectionText = styled.p`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin: 0 0 16px;
`;

const ValidationError = styled(P3)`
  color: ${(props) => props.theme.colors.negative};
  margin-bottom: 0;
  margin-top: 6px;
`;

const AmountInputRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 10px;
`;

const CurrentAmountChip = styled.span`
  align-items: center;
  color: ${(props) => props.theme.colors.tertiaryBrand};
  display: flex;
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  height: 42px;
  white-space: nowrap;
`;

const ChipArrow = styled.span`
  color: ${(props) => props.theme.colors.dividerDark};
  font-size: 16px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const LeftAlignedAction = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    margin: 0;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ClaimItemEditModal: React.FC<ClaimItemEditModalProps> = ({
  claimItemId,
  currentAmount,
  title,
  itemType,
  claimSummary,
  pendingPreview,
  isPreviewing = false,
  isConfirming = false,
  isEditDisabled = false,
  onReduce,
  onRemove,
  onConfirm,
  onGoBack,
  onDismiss,
}) => {
  const [amountValue, setAmountValue] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const fmt = (pence: number) => `£${numeral(pence / 100).format(NumberFormat.THOUSANDS_PENCE)}`;
  const formattedCurrentAmount = fmt(currentAmount);

  const trimmed = amountValue.trim();
  const isFormatValid = trimmed !== '' && /^(?:\d+|\d+\.\d{1,2})$/.test(trimmed);
  const nextAmount = isFormatValid ? Math.round(Number(trimmed) * 100) : undefined;

  const getValidationError = (): string | null => {
    if (trimmed === '') return null;
    if (!isFormatValid) return 'Please enter a valid amount (e.g. 10.00).';
    if (nextAmount !== undefined && nextAmount <= 0) return 'Use the "Remove item" section below to remove this item.';
    if (nextAmount !== undefined && nextAmount >= currentAmount) return 'New amount must be less than the current amount.';
    return null;
  };

  const validationError = isDirty ? getValidationError() : null;
  const canSubmit = isFormatValid && nextAmount !== undefined && nextAmount > 0 && nextAmount < currentAmount;
  const isFormDisabled = isPreviewing || isConfirming || isEditDisabled;
  const isCloseDisabled = isPreviewing || isConfirming;

  const formatClaimStatus = (status: string) => startCase(toLower(status.replace(/_/g, ' ')));

  const renderClaimCard = (p: PendingInternalUpdate['preview']) => {
    const willDeactivate = p.willDeactivateClaim;
    const currentStatus = claimSummary && claimSummary.status ? formatClaimStatus(claimSummary.status) : undefined;

    return (
      <ItemSummary>
        <CardTypePill>Claim</CardTypePill>
        <ItemSummaryTopRow>
          <ItemSummaryLeft>
            <ItemSummaryName>{claimSummary ? claimSummary.address : 'Claim'}</ItemSummaryName>
            {claimSummary && (
              <ItemSummarySubRow>
                <IdentifierValue>{claimSummary.id}</IdentifierValue>
                {!willDeactivate && currentStatus ? ` · ${currentStatus}` : null}
              </ItemSummarySubRow>
            )}
          </ItemSummaryLeft>
          <ClaimSummaryRight>
            {willDeactivate && (
              <StatusChange>
                {currentStatus && (
                  <>
                    <SummaryOldAmount>{currentStatus}</SummaryOldAmount>
                    <SummaryArrow>→</SummaryArrow>
                  </>
                )}
                Deactivated
              </StatusChange>
            )}
            <ItemSummaryAmount style={{ marginLeft: 0 }}>
              <SummaryOldAmount>{fmt(p.currentClaimAmount)}</SummaryOldAmount>
              <SummaryArrow>→</SummaryArrow>
              {fmt(p.projectedClaimAmount)}
            </ItemSummaryAmount>
          </ClaimSummaryRight>
        </ItemSummaryTopRow>
      </ItemSummary>
    );
  };

  const renderItemSummary = (projectedAmount?: number, deleted?: boolean) => (
    <ItemSummary>
      <CardTypePill>Claim item</CardTypePill>
      <ItemSummaryTopRow>
        <ItemSummaryLeft>
          <ItemSummaryName deleted={deleted}>{title || <IdentifierValue>{claimItemId}</IdentifierValue>}</ItemSummaryName>
          <ItemSummarySubRow deleted={deleted}>
            <IdentifierValue>{claimItemId}</IdentifierValue>
            {itemType ? ` · ${startCase(toLower(itemType))}` : null}
          </ItemSummarySubRow>
        </ItemSummaryLeft>
        <ItemSummaryAmount deleted={deleted}>
          {projectedAmount !== undefined ? (
            <>
              <SummaryOldAmount>{formattedCurrentAmount}</SummaryOldAmount>
              <SummaryArrow>→</SummaryArrow>
              {fmt(projectedAmount)}
            </>
          ) : (
            formattedCurrentAmount
          )}
        </ItemSummaryAmount>
      </ItemSummaryTopRow>
    </ItemSummary>
  );

  // ── Preview step ────────────────────────────────────────────────────────
  if (pendingPreview) {
    const isDeletion = pendingPreview.preview.action === ClaimItemCorrectionResponseDTOActionEnum.Deletion;
    const p = pendingPreview.preview;

    return (
      <Modal onDismiss={onDismiss}>
        <Header3 style={{ marginBottom: 4 }}>{isDeletion ? 'Remove Claim Item' : 'Update Claim Item Amount'}</Header3>
        <P2 style={{ marginBottom: 20 }}>
          {isDeletion
            ? 'Confirm you want to remove this claim item. Review the impact below before proceeding.'
            : 'Confirm you want to reduce this claim item amount. Review the impact below before proceeding.'}
        </P2>

        {isDeletion ? (
          <>
            <SectionDivider deletion>
              <SectionDividerText deletion>Deleted</SectionDividerText>
            </SectionDivider>
            {renderItemSummary(undefined, true)}
            <SectionDivider>
              <SectionDividerText>Modified</SectionDividerText>
            </SectionDivider>
            {renderClaimCard(p)}
            {p.willDeactivateClaim && (
              <DeactivationWarning>
                Removing the last item will deactivate this claim and email the agent and tenants.
              </DeactivationWarning>
            )}
          </>
        ) : (
          <>
            <SectionDivider>
              <SectionDividerText>Modified</SectionDividerText>
            </SectionDivider>
            {renderItemSummary(p.projectedItemAmount)}
            {renderClaimCard(p)}
          </>
        )}

        <ActionBar>
          <Button buttonType="secondary" onClick={onGoBack} disabled={isConfirming} noArrow>
            Go back
          </Button>
          <Button buttonType={isDeletion ? 'warning' : undefined} onClick={onConfirm} disabled={isConfirming}>
            {isDeletion ? 'Remove' : 'Confirm'}
          </Button>
        </ActionBar>
      </Modal>
    );
  }

  // ── Edit step ────────────────────────────────────────────────────────────
  return (
    <Modal onDismiss={onDismiss}>
      <Header3 style={{ marginBottom: 16 }}>Modify Claim Item</Header3>

      {renderItemSummary()}

      <Divider />

      <SectionTitle>Reduce amount</SectionTitle>
      <SectionText>Enter a new amount below. You can only reduce the current value.</SectionText>

      <AmountInputRow>
        <CurrentAmountChip>
          {formattedCurrentAmount}
          <ChipArrow>→</ChipArrow>
        </CurrentAmountChip>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <CurrencyInputWrapper>
            <CurrencySymbol>£</CurrencySymbol>
            <CurrencyInput
              id={`edit-claim-item-${claimItemId}`}
              type="number"
              value={amountValue}
              onChange={(e) => {
                setAmountValue(e.target.value);
                setIsDirty(true);
              }}
              placeholder="0.00"
              disabled={isFormDisabled}
              min="0"
              step="0.01"
            />
          </CurrencyInputWrapper>
          {validationError && <ValidationError>{validationError}</ValidationError>}
        </div>
      </AmountInputRow>

      <ActionBar>
        <Button onClick={() => onReduce(claimItemId, nextAmount!)} disabled={isFormDisabled || !canSubmit}>
          Update amount
        </Button>
      </ActionBar>

      <Divider />

      <SectionTitle>Remove item</SectionTitle>
      <SectionText>This item will be moved to deleted claim items.</SectionText>
      <ActionBar>
        <Button buttonType="warning" onClick={() => onRemove(claimItemId)} disabled={isFormDisabled} noArrow>
          Remove Item
        </Button>
      </ActionBar>

      <Divider />

      <LeftAlignedAction>
        <Button buttonType="secondary" onClick={onDismiss} disabled={isCloseDisabled} noArrow>
          Close
        </Button>
      </LeftAlignedAction>
    </Modal>
  );
};

export default ClaimItemEditModal;
